import React from "react";
import PropTypes from "prop-types";
import Link from "next/link";
import styled, { useTheme } from "styled-components";
import { kebabCase } from "lodash";
import RightArrow from "components/icons/rightarrow";
import BusinessCard from "components/HomepageFeaturedBusinesses/HomepageFeaturedBusinessCard";

const OuterWrapper = styled.section`
  display: flex;
  justify-content: center;
  padding: 0;

  ${({ theme }) => theme.mq.xxl`
    justify-content: space-between;
    padding: 0 0 3.25rem;
    width: 100%;
  `}
`;

const InnerWrapper = styled.div`
  background-color: ${({ theme }) => theme.themeColors.white};
  margin: 0 auto;
  max-width: 80.3125rem;
  width: 100%;

  ${({ theme }) => theme.mq.xxl`
    display: flex;
    flex-direction: column;
    border-radius: 2rem;
    padding: 3rem 3.5rem;
    width: calc(100% - 3rem);
  `}

  :after {
    border-bottom: 0.125rem solid #e0e3e0;
    content: " ";
    display: block;
    margin: 0 0 3rem;
    position: relative;
    left: 1.5rem;
    width: calc(100% - 3rem);

    ${({ theme }) => theme.mq.xxl`
      display: none;
    `}
  }
`;

const HeaderWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 0.5rem 0 1.5rem 0;
  padding: 0 1.5rem;

  ${({ theme }) => theme.mq.xxl`
    margin: 0 0 2.1875rem;
    padding: 0;
  `}
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.themeColors.darkGrey};
  font-size: 2rem;
  letter-spacing: -0.033125rem;
  line-height: 1.1;
  margin-bottom: 0.5rem;

  ${({ theme }) => theme.mq.md`
    br {
      display: none;
    }
  `}

  ${({ theme }) => theme.mq.xxl`
    font-size: 2.25rem;
  `}
`;

const TitlesWrapper = styled.div``;

const StyledLink = styled.a`
  font-size: ${({ theme }) => theme.fontSize(1)};
  font-weight: 600;
  line-height: 1.0625rem;
  margin-top: 0.625rem;
  white-space: nowrap;

  :hover {
    cursor: pointer;
    opacity: 0.8;
  }
  transition: ${({ theme }) => theme.opacityTransition};

  ${({ theme }) => theme.mq.xxl`
    align-self: flex-end;
    margin-top: 0;
  `}
`;

const SeeAllLinkWrapper = styled.div`
  color: ${({ theme }) => theme.themeColors.darkGrey};
  display: ${(props) => (props.isBelowCarousel && "flex") || "none"};
  padding: 0 1.5rem;
  margin: 0 0 1.5rem;

  ${({ theme }) => theme.mq.xxl`
    display: ${(props) => (props.isBelowCarousel && "none") || "flex"};
    margin: 0;
  `}
`;

const StyledRightArrow = styled(RightArrow)`
  margin: 0 0 0 0.75rem;
`;

const CarouselScrollWrapper = styled.div`
  position: relative;
  max-width: 100vw;
  overflow-x: auto;
`;

const BusinessesCarousel = styled.ul`
  display: flex;
  min-height: 17.9375rem;
  padding: 0 0 0 1.4375rem;
  margin: 0 1.5rem 0 0;

  ${({ theme }) => theme.mq.lg`
    width: calc(100% - 3rem);
    max-width: 80.3125rem;
  `}

  ${({ theme }) => theme.mq.xxl`
    margin: 0;
    padding: 0;
    width: 100%;
  `}
`;

const Spacer = styled.div`
  display: inline-block;
  width: 1.5rem;

  ${({ theme }) => theme.mq.xxl`
    display: none;
  `}
`;

function HomepageFeaturedBusinessesComponent({
  title,
  businessType,
  viewAllLink,
  businessList,
}) {
  const theme = useTheme();
  return (
    <OuterWrapper>
      <InnerWrapper>
        <HeaderWrapper>
          <TitlesWrapper>
            <Title>{title}</Title>
          </TitlesWrapper>
          <SeeAllLinkWrapper>
            <Link href={viewAllLink} passHref>
              <StyledLink>
                <span>See all nearby {businessType}</span>
                <StyledRightArrow
                  color={theme.themeColors.darkGrey}
                  height={8}
                />
              </StyledLink>
            </Link>
          </SeeAllLinkWrapper>
        </HeaderWrapper>
        <CarouselScrollWrapper>
          <BusinessesCarousel>
            {businessList?.map((biz) => {
              return (
                <BusinessCard
                  key={`${biz.id}-${kebabCase(biz.attributes.businessName)}`}
                  business={biz}
                />
              );
            })}
          </BusinessesCarousel>
          <Spacer />
        </CarouselScrollWrapper>
        <SeeAllLinkWrapper isBelowCarousel>
          <Link href={viewAllLink} passHref>
            <StyledLink>
              <span>See all nearby {businessType}</span>
              <StyledRightArrow color={theme.themeColors.darkGrey} height={8} />
            </StyledLink>
          </Link>
        </SeeAllLinkWrapper>
      </InnerWrapper>
    </OuterWrapper>
  );
}

export default HomepageFeaturedBusinessesComponent;

HomepageFeaturedBusinessesComponent.propTypes = {
  businessType: PropTypes.string,
  title: PropTypes.shape({}),
  viewAllLink: PropTypes.string,
  businessList: PropTypes.arrayOf(PropTypes.shape({})),
};

HomepageFeaturedBusinessesComponent.defaultProps = {
  businessList: [],
  businessType: "",
  title: "",
  viewAllLink: "/",
};
